import type { AppContext, AppProps, NextWebVitalsMetric } from 'next/app';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';

import SharedProvider from '@invideoio/web-shared/context/SharedProvider/SharedProvider';
import { PageType } from '@invideoio/web-shared/types/Analytics';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';

import {
  BrowserType,
  PlatformType,
} from '@invideoio/web-shared/constants/common';
import { Auth } from '@invideoio/web-shared/lib/API/auth';
import { FeatureConfig } from '@invideoio/web-shared/types/Config';
import {
  LOCAL_STORAGE,
  URL_SEARCH_PARAM,
} from '@invideoio/web-shared/utils/utils.constant';
import { fetchConfig, fetchFeatureJson } from '@lib/API';
import store from '@store/store';
import '@styles/globals.scss';
import { AmplitudeEvents, sendAmplitudeAnalyticEvent } from '@utils/analytics';
import { localStorageWithExpiry } from '@utils/localStorageUtils';
import App from 'next/app';
import dynamic from 'next/dynamic';
import { Inter } from 'next/font/google';
import { ConfigType, SocialLinksType } from '../types';

// If loading a variable font, you don't need to specify the font weight
const inter = Inter({
  subsets: ['latin'],
  weight: ['300', '400', '500', '600', '700', '800'],
  display: 'swap',
});


const SharedModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SharedModal" */
      '@components/SharedModal/SharedModal'
    ),
  {
    ssr: false,
  },
);

const LoadAnalytics = dynamic(
  () =>
    import(
      /* webpackChunkName: "Header" */
      '@components/LoadAnalytics/LoadAnalytics'
    ),
  {
    ssr: false,
  },
);

export function reportWebVitals(metric: NextWebVitalsMetric) {
  console.log(metric);
}

export interface RootProps extends AppProps {
  config: ConfigType;
  featureConfig: FeatureConfig;
  platform: PlatformType;
  os: string;
  socialLinks: SocialLinksType;
  browser: BrowserType;
  userAgent: string;
  isGoogleBot: boolean;
}

interface MyWindow extends Window {
  nua?: string;
}

function RootApp({ Component, pageProps, ...otherProps }: RootProps) {
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    (window as unknown as MyWindow).nua = otherProps.userAgent;

    AnalyticsUtils.registerPage(PageType.TOOLS, {
      location: window.location.href,
    });
    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad);

    const search_params = window.location.search;
    const params = new RegExp(Object.values(URL_SEARCH_PARAM).join('|'));

    if (search_params !== '' && params.test(search_params)) {
      localStorageWithExpiry.set('qs', search_params, 60 * 24);
    }

    const domain_referrer = document.referrer;
    if (
      domain_referrer !== '' &&
      !domain_referrer.match(/invideo\.io|localhost/)
    ) {
      localStorageWithExpiry.set('domain_referrer', domain_referrer, 60 * 24);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem(LOCAL_STORAGE.AUTH_TOKEN)) {
      Auth.getCompleteUserDetails().then(response => {
        const data = response.data;

        if (data) {
          setUserData(data);
        }
      });
    }
  }, []);

  return (
    <SharedProvider
      config={{
        RECAPTCHA_KEY: process.env.NEXT_PUBLIC_GOOGLE_CAPTCHA_KEY || '',
        siteConfig: otherProps.config,
        API: {
          ANALYTICS_API: process.env.NEXT_PUBLIC_ANALYTICS_API_URL || '',
          AUTH_API: process.env.NEXT_PUBLIC_AUTH_API || '',
          CORE_API: process.env.NEXT_PUBLIC_API_URL || '',
        },
        APP_ENV: process.env.NEXT_PUBLIC_ENV || 'prodcution',
        CDN_URL: {
          SHARED_URL: process.env.NEXT_PUBLIC_SHARED_CDN_URL || '',
        },
        HOST_URL: process.env.NEXT_PUBLIC_BASE_HOST || '',
        CREATE_PAGE_HOST_URL: process.env.NEXT_PUBLIC_CREATE_PAGE_HOST || '',
      }}
      user={userData}
      featureConfig={otherProps.featureConfig}
    >
      <Provider store={store}>
        <main className={inter.className}>
          <Component {...pageProps} {...otherProps} />
        </main>
        <SharedModal />
        <LoadAnalytics
          userData={userData}
          platform={otherProps.platform}
          isGoogleBot={otherProps.isGoogleBot}
        />
      </Provider>
    </SharedProvider>
  );
}

RootApp.getInitialProps = async (appContext: AppContext): Promise<any> => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);

  try {
    const deviceDetect = require('../middleware/deviceDetect').default;
    const { os, platform, browser, userAgent, isGoogleBot } =
      await deviceDetect(appContext.ctx);

    const config = await fetchConfig();

    const featureConfig = await fetchFeatureJson();

    const socialLinks = config.social;

    return {
      ...appProps,
      os,
      platform,
      browser,
      userAgent,
      isGoogleBot,
      socialLinks,
      config,
      featureConfig,
    };
  } catch (error) {
    console.error(error);
    return { ...appProps };
  }
};

export default RootApp;
